/**
 * ContactRowAvatar - Small Gravatar avatar for contact table rows.
 *
 * Shows a Gravatar image for the contact's email address.
 * Falls back to initials derived from the contact's name.
 */

import { memo, useEffect, useState } from 'react';

import { getAvatarInitials, getGravatarUrl } from '@/lib/crm/utils/avatar';

interface ContactRowAvatarProps {
  /** Email address for Gravatar lookup (null if no email available) */
  email: string | null;
  /** Display name for initials fallback */
  name: string;
}

/**
 * Compact avatar component for table rows.
 * Tries Gravatar first, falls back to colored initials circle.
 */
export const ContactRowAvatar = memo(function ContactRowAvatar({
  email,
  name,
}: ContactRowAvatarProps) {
  const [showFallback, setShowFallback] = useState(false);

  // Reset fallback state when email changes
  useEffect(() => {
    setShowFallback(false);
  }, [email]);

  const initials = getAvatarInitials(name || (email?.split('@')[0] ?? ''));

  // If no email or Gravatar failed, show initials
  if (!email || showFallback) {
    return (
      <div
        className='size-7 rounded-full bg-neutral-700 dark:bg-neutral-600 flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0'
        title={email ?? name}
      >
        {initials || '?'}
      </div>
    );
  }

  return (
    <div
      className='size-7 rounded-full overflow-hidden bg-primary border border-primary flex-shrink-0'
      title={email}
    >
      <img
        src={getGravatarUrl(email, 56)}
        alt={name || email}
        className='size-full object-cover'
        onError={() => {
          setShowFallback(true);
        }}
      />
    </div>
  );
});
